import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

export function AuthMenu() {
    const { user, logout } = useAuth();
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    if (user === null) return <div className="w-24 h-10" />; // checking placeholder

    if (!user) {
        return (
            <Link
                to="/login"
                data-testid="header-signin"
                className="eyebrow inline-flex items-center rounded-full bg-obsidian text-white px-5 py-2.5 hover:-translate-y-0.5 hover:bg-cyan hover:text-obsidian transition-all duration-300"
            >
                {t.auth.signIn}
            </Link>
        );
    }

    const initial = (user.username || user.name || "?").slice(0, 1).toUpperCase();
    const doLogout = async () => { await logout(); setOpen(false); navigate("/"); };
    const Avatar = ({ size }) => (
        user.picture ? (
            <img
                src={user.picture}
                alt={user.name || "profile"}
                referrerPolicy="no-referrer"
                data-testid="profile-avatar"
                className="rounded-full object-cover"
                style={{ width: size, height: size }}
            />
        ) : (
            <span
                data-testid="profile-avatar-initial"
                className="grid place-items-center rounded-full bg-cyan text-obsidian font-bold"
                style={{ width: size, height: size, fontSize: size * 0.42 }}
            >
                {initial}
            </span>
        )
    );

    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="profile-menu-button"
                onClick={() => setOpen((v) => !v)}
                className="eyebrow flex items-center gap-2 rounded-full bg-obsidian text-white pl-1.5 pr-4 py-1.5 hover:bg-cyan hover:text-obsidian transition-colors duration-300"
            >
                <Avatar size={26} />
                {user.username || user.name}
                <ChevronDown size={14} />
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-60 glass border border-border rounded-lg overflow-hidden shadow-lg z-50" data-testid="profile-menu">
                    <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                        <Avatar size={40} />
                        <div className="min-w-0">
                            <div className="font-semibold text-obsidian text-sm truncate" data-testid="profile-name">{user.name}</div>
                            <div className="text-xs text-muted-foreground font-mono-tech truncate">{user.email}</div>
                        </div>
                    </div>
                    {user.username && <div className="px-4 pt-2 eyebrow text-cyan" data-testid="profile-username">@{user.username}</div>}
                    {user.role === "admin" && (
                        <Link to="/admin" data-testid="profile-dashboard" className="flex items-center gap-3 px-4 py-3 text-sm text-obsidian hover:bg-secondary transition-colors">
                            <LayoutDashboard size={15} /> {t.auth.dashboard}
                        </Link>
                    )}
                    <button data-testid="profile-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-destructive hover:bg-secondary transition-colors">
                        <LogOut size={15} /> {t.auth.logout}
                    </button>
                </div>
            )}
        </div>
    );
}
